// Blocking stdin for workers: a single-producer (page) / single-consumer (worker) byte ring in a
// SharedArrayBuffer. The worker sleeps in Atomics.wait until bytes arrive or input is closed.

const WRITE = 0; // total bytes ever written
const READ = 1; // total bytes ever read
const CLOSED = 2; // 1 once the writer closed (EOF)
const SIGNAL = 3; // bumped on every write/close, for Atomics.wait/notify
const HEADER_INTS = 4;

export const DEFAULT_STDIN_CAPACITY = 64 * 1024;

export function createStdinBuffer(capacity = DEFAULT_STDIN_CAPACITY): SharedArrayBuffer {
  return new SharedArrayBuffer(HEADER_INTS * 4 + capacity);
}

const views = (buffer: SharedArrayBuffer) => ({
  header: new Int32Array(buffer, 0, HEADER_INTS),
  data: new Uint8Array(buffer, HEADER_INTS * 4),
});

/** Page side. Writes never block: bytes that don't fit are queued and flushed as the worker reads. */
export class StdinWriter {
  readonly #header: Int32Array;
  readonly #data: Uint8Array;
  #pending: Uint8Array[] = [];
  #timer: ReturnType<typeof setTimeout> | undefined;
  #closeRequested = false;

  constructor(buffer: SharedArrayBuffer) {
    ({ header: this.#header, data: this.#data } = views(buffer));
  }

  write(bytes: Uint8Array): void {
    if (this.#closeRequested) return;
    if (bytes.byteLength > 0) this.#pending.push(bytes);
    this.#flush();
  }

  close(): void {
    this.#closeRequested = true;
    this.#flush();
  }

  dispose(): void {
    clearTimeout(this.#timer);
  }

  #flush(): void {
    clearTimeout(this.#timer);
    const capacity = this.#data.byteLength;
    while (this.#pending.length > 0) {
      const write = Atomics.load(this.#header, WRITE);
      const free = capacity - (write - Atomics.load(this.#header, READ));
      if (free === 0) break;
      const chunk = this.#pending[0] as Uint8Array;
      const n = Math.min(free, chunk.byteLength);
      for (let i = 0; i < n; i++) this.#data[(write + i) % capacity] = chunk[i] as number;
      Atomics.store(this.#header, WRITE, write + n);
      if (n === chunk.byteLength) this.#pending.shift();
      else this.#pending[0] = chunk.subarray(n);
      this.#signal();
    }
    if (this.#pending.length > 0) {
      this.#timer = setTimeout(() => this.#flush(), 10); // the worker hasn't read yet
    } else if (this.#closeRequested && Atomics.load(this.#header, CLOSED) === 0) {
      Atomics.store(this.#header, CLOSED, 1);
      this.#signal();
    }
  }

  #signal(): void {
    Atomics.add(this.#header, SIGNAL, 1);
    Atomics.notify(this.#header, SIGNAL);
  }
}

/** Worker side. `read` blocks until input is available; null means end of input. */
export class StdinReader {
  readonly #header: Int32Array;
  readonly #data: Uint8Array;
  #lineBuffer = new Uint8Array(0);

  constructor(buffer: SharedArrayBuffer) {
    ({ header: this.#header, data: this.#data } = views(buffer));
  }

  /** Up to `max` bytes; waits for at least one. Null at EOF. Must not run on a browser main thread. */
  read(max = 4096): Uint8Array | null {
    for (;;) {
      const signal = Atomics.load(this.#header, SIGNAL);
      const read = Atomics.load(this.#header, READ);
      const available = Atomics.load(this.#header, WRITE) - read;
      if (available > 0) {
        const n = Math.min(available, max);
        const out = new Uint8Array(n);
        const capacity = this.#data.byteLength;
        for (let i = 0; i < n; i++) out[i] = this.#data[(read + i) % capacity] as number;
        Atomics.store(this.#header, READ, read + n);
        return out;
      }
      if (Atomics.load(this.#header, CLOSED) === 1) return null;
      Atomics.wait(this.#header, SIGNAL, signal);
    }
  }

  /** One line without its "\n" (decoded as UTF-8). Null at EOF with nothing buffered. */
  readLine(): string | null {
    const decoder = new TextDecoder();
    for (;;) {
      const newline = this.#lineBuffer.indexOf(10);
      if (newline >= 0) {
        const line = this.#lineBuffer.subarray(0, newline);
        this.#lineBuffer = this.#lineBuffer.slice(newline + 1);
        return decoder.decode(line).replace(/\r$/, "");
      }
      const chunk = this.read();
      if (chunk === null) {
        if (this.#lineBuffer.byteLength === 0) return null;
        const rest = this.#lineBuffer;
        this.#lineBuffer = new Uint8Array(0);
        return decoder.decode(rest);
      }
      const joined = new Uint8Array(this.#lineBuffer.byteLength + chunk.byteLength);
      joined.set(this.#lineBuffer);
      joined.set(chunk, this.#lineBuffer.byteLength);
      this.#lineBuffer = joined;
    }
  }
}
